const STORAGE = 'spese-auth';

export type AuthSession = {
  idToken: string;
  accessToken: string;
  refreshToken: string;
};

export type LoginStartResult =
  | { status: 'authenticated'; session: AuthSession }
  | { status: 'software_token_mfa'; username: string; cognitoSession: string }
  | { status: 'sms_mfa'; username: string; cognitoSession: string }
  | { status: 'new_password_required'; username: string; cognitoSession: string };

function region() {
  return import.meta.env.VITE_AWS_REGION || 'eu-west-1';
}

function clientId() {
  const id = import.meta.env.VITE_USER_POOL_CLIENT_ID;
  if (!id) throw new Error('VITE_USER_POOL_CLIENT_ID mancante');
  return id;
}

function cognitoUrl() {
  return `https://cognito-idp.${region()}.amazonaws.com/`;
}

async function cognitoCall(target: string, body: Record<string, unknown>) {
  const res = await fetch(cognitoUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let data: Record<string, unknown> = {};
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      /* ignore */
    }
  }
  if (!res.ok) {
    const msg = (data.__type as string) || (data.message as string) || text || res.statusText;
    throw new Error(typeof msg === 'string' ? msg : 'Errore Cognito');
  }
  return data;
}

function tokensFromAuthResult(auth: Record<string, string> | undefined): AuthSession | null {
  if (!auth?.IdToken || !auth?.AccessToken) return null;
  return {
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: auth.RefreshToken || '',
  };
}

function parseAuthResponse(data: Record<string, unknown>, username: string): LoginStartResult {
  const challenge = data.ChallengeName as string | undefined;
  const sess = data.Session as string | undefined;

  if (challenge && sess) {
    if (challenge === 'SOFTWARE_TOKEN_MFA') {
      return { status: 'software_token_mfa', username, cognitoSession: sess };
    }
    if (challenge === 'SMS_MFA') {
      return { status: 'sms_mfa', username, cognitoSession: sess };
    }
    if (challenge === 'NEW_PASSWORD_REQUIRED') {
      return { status: 'new_password_required', username, cognitoSession: sess };
    }
    if (challenge === 'MFA_SETUP') {
      throw new Error(
        'Devi completare la configurazione MFA da Cognito (MFA_SETUP). Usa la console AWS o l’app ufficiale.'
      );
    }
    throw new Error(
      `Login bloccato da Cognito (challenge: ${challenge}). Contatta l’amministratore o usa la console.`
    );
  }

  const auth = data.AuthenticationResult as Record<string, string> | undefined;
  const session = tokensFromAuthResult(auth);
  if (!session) {
    const keys = Object.keys(data).join(', ');
    throw new Error(
      `Risposta Cognito imprevista (nessun token né challenge). Campi: ${keys || 'vuoto'}. Verifica regione (${region()}) e Client ID.`
    );
  }
  if (!session.refreshToken) {
    throw new Error(
      'Cognito non ha restituito il refresh token. Controlla le impostazioni dell’App client nel pool (refresh token consentito).'
    );
  }
  return { status: 'authenticated', session };
}

export function loadSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE);
    if (!raw) return null;
    const s = JSON.parse(raw) as AuthSession;
    if (s?.idToken && s?.refreshToken) return s;
    return null;
  } catch {
    return null;
  }
}

export function saveSession(s: AuthSession) {
  localStorage.setItem(STORAGE, JSON.stringify(s));
}

export function clearSession() {
  localStorage.removeItem(STORAGE);
}

/** Primo passo: email + password (gestisce MFA e cambio password obbligatorio). */
export async function loginWithPassword(email: string, password: string): Promise<LoginStartResult> {
  const username = email.trim();
  const data = await cognitoCall('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: clientId(),
    AuthParameters: {
      USERNAME: username,
      PASSWORD: password,
    },
  });
  const result = parseAuthResponse(data, username);
  if (result.status === 'authenticated') {
    saveSession(result.session);
  }
  return result;
}

export async function completeSoftwareTokenMfa(
  username: string,
  cognitoSession: string,
  code: string
): Promise<AuthSession> {
  const data = await cognitoCall('RespondToAuthChallenge', {
    ChallengeName: 'SOFTWARE_TOKEN_MFA',
    ClientId: clientId(),
    Session: cognitoSession,
    ChallengeResponses: {
      USERNAME: username.trim(),
      SOFTWARE_TOKEN_MFA_CODE: code.trim().replace(/\s/g, ''),
    },
  });
  const result = parseAuthResponse(data, username.trim());
  if (result.status !== 'authenticated') {
    throw new Error('Dopo MFA Cognito ha richiesto un altro passaggio. Riprova.');
  }
  saveSession(result.session);
  return result.session;
}

export async function completeSmsMfa(
  username: string,
  cognitoSession: string,
  code: string
): Promise<AuthSession> {
  const data = await cognitoCall('RespondToAuthChallenge', {
    ChallengeName: 'SMS_MFA',
    ClientId: clientId(),
    Session: cognitoSession,
    ChallengeResponses: {
      USERNAME: username.trim(),
      SMS_MFA_CODE: code.trim().replace(/\s/g, ''),
    },
  });
  const result = parseAuthResponse(data, username.trim());
  if (result.status !== 'authenticated') {
    throw new Error('Dopo MFA Cognito ha richiesto un altro passaggio. Riprova.');
  }
  saveSession(result.session);
  return result.session;
}

export async function completeNewPassword(
  username: string,
  cognitoSession: string,
  newPassword: string
): Promise<LoginStartResult> {
  const data = await cognitoCall('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: clientId(),
    Session: cognitoSession,
    ChallengeResponses: {
      USERNAME: username.trim(),
      NEW_PASSWORD: newPassword,
    },
  });
  return parseAuthResponse(data, username.trim());
}

/** @deprecated Usa loginWithPassword */
export async function signIn(email: string, password: string): Promise<AuthSession> {
  const r = await loginWithPassword(email, password);
  if (r.status === 'authenticated') return r.session;
  if (r.status === 'software_token_mfa' || r.status === 'sms_mfa') {
    throw new Error('È richiesto il codice MFA (app authenticator o SMS). Completa il secondo passaggio.');
  }
  if (r.status === 'new_password_required') {
    throw new Error('Devi impostare una nuova password (account con password temporanea).');
  }
  throw new Error('Login non completato');
}

export async function refreshSession(refreshToken: string): Promise<AuthSession> {
  const data = await cognitoCall('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: clientId(),
    AuthParameters: {
      REFRESH_TOKEN: refreshToken,
    },
  });
  const auth = data.AuthenticationResult as Record<string, string> | undefined;
  const prev = loadSession();
  if (!auth?.IdToken || !auth?.AccessToken) {
    throw new Error('Refresh fallito');
  }
  const session: AuthSession = {
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: prev?.refreshToken || refreshToken,
  };
  saveSession(session);
  return session;
}

export type CognitoProfile = {
  email: string;
  name: string;
  avatar: string;
};

function attrMap(attrs: { Name: string; Value: string }[]) {
  const m: Record<string, string> = {};
  for (const a of attrs) m[a.Name] = a.Value;
  return m;
}

export async function getUserProfile(accessToken: string): Promise<CognitoProfile> {
  const data = await cognitoCall('GetUser', { AccessToken: accessToken });
  const attrs = (data.UserAttributes as { Name: string; Value: string }[]) || [];
  const m = attrMap(attrs);
  return {
    email: m.email || '',
    name: m.name || '',
    avatar: m['custom:avatar'] || '',
  };
}

export async function updateUserAttributes(
  accessToken: string,
  updates: { name?: string; avatar?: string | null }
): Promise<void> {
  const UserAttributes: { Name: string; Value: string }[] = [];
  if (updates.name !== undefined) {
    UserAttributes.push({ Name: 'name', Value: updates.name });
  }
  if (updates.avatar !== undefined) {
    UserAttributes.push({
      Name: 'custom:avatar',
      Value: updates.avatar == null ? '' : updates.avatar,
    });
  }
  if (UserAttributes.length === 0) return;
  await cognitoCall('UpdateUserAttributes', {
    AccessToken: accessToken,
    UserAttributes,
  });
}

export async function changePassword(
  accessToken: string,
  previousPassword: string,
  proposedPassword: string
): Promise<void> {
  await cognitoCall('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

export async function deleteUserAttributes(accessToken: string, attributeNames: string[]): Promise<void> {
  if (attributeNames.length === 0) return;
  await cognitoCall('DeleteUserAttributes', {
    AccessToken: accessToken,
    UserAttributeNames: attributeNames,
  });
}

/** Es. `SOFTWARE_TOKEN_MFA`, `SMS_MFA` — da `GetUser`. */
export async function getUserMfaMethods(accessToken: string): Promise<string[]> {
  const data = await cognitoCall('GetUser', { AccessToken: accessToken });
  const list = data.UserMFASettingList as string[] | undefined;
  return Array.isArray(list) ? list : [];
}

export async function associateSoftwareToken(accessToken: string): Promise<string> {
  const data = await cognitoCall('AssociateSoftwareToken', { AccessToken: accessToken });
  const secret = data.SecretCode as string | undefined;
  if (!secret) throw new Error('Cognito non ha restituito SecretCode');
  return secret;
}

export async function verifySoftwareToken(
  accessToken: string,
  userCode: string,
  friendlyDeviceName?: string
): Promise<void> {
  const body: Record<string, unknown> = {
    AccessToken: accessToken,
    UserCode: userCode.trim().replace(/\s/g, ''),
  };
  if (friendlyDeviceName?.trim()) body.FriendlyDeviceName = friendlyDeviceName.trim();
  await cognitoCall('VerifySoftwareToken', body);
}

export async function disassociateSoftwareToken(accessToken: string): Promise<void> {
  await cognitoCall('DisassociateSoftwareToken', { AccessToken: accessToken });
}

/** URI `otpauth://` per QR / app authenticator (secret in Base32 come da Cognito). */
export function buildTotpUri(secret: string, accountLabel: string, issuer: string): string {
  const encIssuer = encodeURIComponent(issuer);
  const encLabel = encodeURIComponent(accountLabel);
  return `otpauth://totp/${encIssuer}:${encLabel}?secret=${secret}&issuer=${encIssuer}`;
}
